import { Component, ElementRef, EventEmitter, forwardRef, HostListener, inject, Input, Output, Signal, ViewChild } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms'
import { SearchOption } from '../../../models/types/components/Search.type';
import { LayoutService } from '@services/layout.service';
import { TranslationService } from '@services/translation.service';

@Component({
    selector: 'app-search-select',
    imports: [
        FormsModule
    ],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchSelectComponent), multi: true }
    ],
    templateUrl: './search-select.component.html',
    styleUrl: './search-select.component.css'
})
export class SearchSelectComponent {

  // VIEW CHIILDS
  @ViewChild('selectContainer')
  public selectContainer!: ElementRef<HTMLDivElement>;

  // INPUTS
  @Input({required: true})
  public options:      SearchOption[] = [];

  @Input()
  public placeholder:  string         = 'Search...';

  @Input()
  public disabled:     boolean        = false;

  @Input()
  public cssClasses:   string[]       = [];

  @Input() 
  public selectedFieldValue: string | null = null;

  // OUTPUTS
  @Output()
  public selectionChange: EventEmitter<SearchOption> = new EventEmitter<SearchOption>();

  // HOST LISTENERS
  @HostListener('document:click', ['$event'])
  public onClick(event: MouseEvent): void {
    if (!this.selectContainer.nativeElement.contains(event.target as Node)) {
      this.openOptions = false;
    }
  }

  //SERVICES
  public readonly translation: TranslationService = inject(TranslationService);
  
  // STATE
  public readonly isMobile: Signal<boolean> = inject(LayoutService).isMobile;
  public value: SearchOption | null       = null;
  public openOptions: boolean             = false;
  public searchText: string               = '';
  public filteredOptions: SearchOption[]  = [];
  public highlightedIndex: number         = -1;

  // GETTERS
  public get cssClass(): string {
   return ['input', ...this.cssClasses].join(' ');
  }

  public get inputLabel(): string {
    return this.value && this.selectedFieldValue ? this.value.label : this.placeholder;
  }

  // METHODS

  public toggleDropdown(): void {
    
    if (this.disabled) return;
    
    this.openOptions = !this.openOptions;
    
    if (this.openOptions) {
      // Resetear la búsqueda cuando se abre
      this.searchText = '';
      this.filteredOptions = [...this.options];
      this.highlightedIndex = -1;
    }
  
  }

  public onSearch(): void { 

    if(!this.searchText.trim()){
      this.filteredOptions = [...this.options];
      return;
    }

    const searchText = this.searchText.toLowerCase();
    this.filteredOptions = this.options.filter(option => option.label.toLowerCase().includes(searchText));

  }

  public onSelect(option: SearchOption): void {
    this.value = option;
    
    this.selectionChange.emit(option);
    this.openOptions = false;
  }

  public onKeydown(event: KeyboardEvent): void {

    // Si no hay resultados abiertos, no se hace nada
    if(!this.openOptions) return;

    
    switch (event.key) {
      
      // Si se pulsa hacia abajo, se suma 1 al índice de la opción resaltada y se comprueba que no sea mayor que el número de opciones filtradas
      case 'ArrowDown':
        
        event.preventDefault();
        
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
        
        if (this.highlightedIndex === -1) this.highlightedIndex = 0;
        
      break;
      
      // Si se pulsa hacia arriba, se resta 1 al índice de la opción resaltada y se comprueba que no sea menor que -1
      case 'ArrowUp':
        
        event.preventDefault();
        
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);

      break;
      
      // Si se pulsa Enter, se selecciona la opción resaltada
      case 'Enter':
        
        event.preventDefault();

        if (this.highlightedIndex !== -1) this.onSelect(this.filteredOptions[this.highlightedIndex]);
        
      break;
      
      // Si se pulsa Escape, se limpia el input
      case 'Escape':
        
        event.preventDefault();

        this.resetInput();
        
      break;
    }
    

  }

  public resetInput(): void {

    this.value              = null; 
    this.openOptions        = false;
    this.searchText         = '';
    this.filteredOptions    = [...this.options];
    this.highlightedIndex   = -1;

  }

}
